import React, { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { addDays, format } from "date-fns"; // Import format function
import enUS from "date-fns/locale/en-US"; // Import the default English locale
import { CloseIcon, BackArrowIcon, CalenderIcon } from "../../assets/icons";
import { getWorkerActivityDates } from "../../services/service";

const customLocale = {
  ...enUS,
  localize: {
    ...enUS.localize,
    day: (n) => ["S", "M", "T", "W", "T", "F", "S"][n],
  },
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MonthYearNav = ({ focusedDate, changeShownDate, minDate, maxDate }) => {
  const [openMonth, setOpenMonth] = useState(false);
  const [openYear, setOpenYear] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenMonth(false);
        setOpenYear(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentMonth = focusedDate.getMonth();
  const currentYear = focusedDate.getFullYear();
  const years = [];
  for (let y = maxDate.getFullYear(); y >= minDate.getFullYear(); y--) years.push(y);

  const dropdownItemClass = (active) =>
    `px-3 py-1.5 text-[13px] cursor-pointer ${active ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white"}`;

  return (
    <div ref={containerRef} className="flex items-center justify-between gap-2 relative mb-2 px-1">
      <button
        type="button"
        onClick={() => changeShownDate(-1, "monthOffset")}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F3F4F6] text-[#1E3A8A] hover:bg-[#E5E7EB]"
      >
        ‹
      </button>

      <div className="flex items-center gap-1">
        <div className="relative">
          <button
            type="button"
            onClick={() => { setOpenMonth((v) => !v); setOpenYear(false); }}
            className="font-semibold text-[14px] text-[#1E3A8A] px-2 py-1 rounded hover:bg-[#F3F4F6] flex items-center gap-1"
          >
            {MONTH_NAMES[currentMonth]}
            <span className="text-[10px]">▾</span>
          </button>
          {openMonth && (
            <div className="absolute z-20 top-full left-0 mt-1 w-36 max-h-52 overflow-y-auto bg-white rounded-lg shadow-lg border border-[#E9E9E9] py-1">
              {MONTH_NAMES.map((name, i) => (
                <div
                  key={name}
                  onClick={() => { changeShownDate(i, "setMonth"); setOpenMonth(false); }}
                  className={dropdownItemClass(i === currentMonth)}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => { setOpenYear((v) => !v); setOpenMonth(false); }}
            className="font-semibold text-[14px] text-[#1E3A8A] px-2 py-1 rounded hover:bg-[#F3F4F6] flex items-center gap-1"
          >
            {currentYear}
            <span className="text-[10px]">▾</span>
          </button>
          {openYear && (
            <div className="absolute z-20 top-full left-0 mt-1 w-20 max-h-52 overflow-y-auto bg-white rounded-lg shadow-lg border border-[#E9E9E9] py-1">
              {years.map((year) => (
                <div
                  key={year}
                  onClick={() => { changeShownDate(year, "setYear"); setOpenYear(false); }}
                  className={dropdownItemClass(year === currentYear)}
                >
                  {year}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => changeShownDate(1, "monthOffset")}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F3F4F6] text-[#1E3A8A] hover:bg-[#E5E7EB]"
      >
        ›
      </button>
    </div>
  );
};

const DailyStatsCalendar = ({ onClose, OpenStats, workerId }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hasPicked, setHasPicked] = useState(false);
  const [activityDates, setActivityDates] = useState([]);

  useEffect(() => {
    if (!workerId) return;
    const fetchActivityDates = async () => {
      try {
        const response = await getWorkerActivityDates(workerId);
        if (response?.success) {
          setActivityDates(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching worker activity dates:", error);
      }
    };
    fetchActivityDates();
  }, [workerId]);

  const isWorkedDate = (date) => activityDates.includes(format(date, "yyyy-MM-dd"));

  const today = new Date();
  const minDate = new Date(today.getFullYear() - 5, 0, 1);
  const maxDate = today;

  const openReportFor = (date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    localStorage.setItem("SelectedDate", formattedDate);
    OpenStats();
    onClose();
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setHasPicked(true);
  };

  const handleApplyClick = () => {
    openReportFor(selectedDate);
  };

  return (
    <div>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: "70%", md: "55%" },
          maxWidth: 640,
          bgcolor: "background.paper",
          boxShadow: "0 20px 60px rgba(30, 58, 138, 0.25)",
          p: 4,
          borderRadius: 3,
        }}
      >
        <div className="relative worker-calendar-fix">
          <style>{`
            .worker-calendar-fix .rdrSelected,
            .worker-calendar-fix .rdrInRange,
            .worker-calendar-fix .rdrStartEdge,
            .worker-calendar-fix .rdrEndEdge {
              background: transparent !important;
            }
            .worker-calendar-fix .rdrDayToday .rdrDayNumber span:after {
              display: none !important;
            }
            .worker-calendar-fix .rdrCalendarWrapper:not(.rdrDateRangeWrapper) .rdrDayHovered .rdrDayNumber:after {
              top: 50% !important;
              bottom: auto !important;
              left: 50% !important;
              right: auto !important;
              width: 28px !important;
              height: 28px !important;
              transform: translate(-50%, -50%) !important;
              border-radius: 50% !important;
              background: #FDEEE3 !important;
              border-color: #F97316 !important;
              z-index: 0 !important;
            }
            .worker-calendar-fix .rdrMonthAndYearWrapper {
              padding-top: 0;
            }
            .worker-calendar-fix .rdrWeekDays {
              background: #F9FAFB;
              border-radius: 10px;
              margin-bottom: 4px;
            }
            .worker-calendar-fix .rdrWeekDay {
              font-weight: 700;
              color: #6D7482;
            }
          `}</style>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-0 left-0 flex items-center gap-1 text-[#1E3A8A] font-medium text-[13px]"
          >
            {BackArrowIcon}
            Back
          </button>
          <div className="flex flex-col items-center gap-1 mt-2 mb-5">
            <div className="[&>svg]:w-10 [&>svg]:h-10">{CalenderIcon}</div>
            <h2 className="font-semibold text-[18px] text-[#1E3A8A]">Select a Date</h2>
            <p className="text-[12px] text-[#6D7482]">Pick a date to view its daily stats</p>
          </div>
          <div className="flex justify-center bg-[#FAFAFB] rounded-2xl p-2">
            <Calendar
              date={selectedDate}
              onChange={handleDateChange}
              color="#F97316"
              locale={customLocale}
              minDate={minDate}
              maxDate={maxDate}
              navigatorRenderer={(focusedDate, changeShownDate) => (
                <MonthYearNav
                  focusedDate={focusedDate}
                  changeShownDate={changeShownDate}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              )}
              dayContentRenderer={(date) => {
                const worked = isWorkedDate(date);
                const selected = hasPicked && format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
                const textColor = worked ? "#F97316" : "#000000";
                return (
                  <span
                    title={worked ? "Worker checked in on this date" : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      border: selected ? "1px solid #F97316" : "1px solid transparent",
                      backgroundColor: selected ? "#FDEEE3" : "transparent",
                      color: textColor,
                      fontWeight: worked ? 700 : 400,
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {date.getDate()}
                  </span>
                );
              }}
            />
          </div>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="contained"
              disabled={!hasPicked}
              sx={{
                width: "100%",
                borderRadius: "999px",
                backgroundColor: "#F97316",
                boxShadow: "none",
                fontWeight: 600,
                textTransform: "none",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: "#ea6a0e",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
              onClick={handleApplyClick}
            >
              Show Results
            </Button>
          </Box>
          <span
            className="absolute cursor-pointer top-[-3.3rem] right-[-3.2rem]"
            onClick={onClose}
          >
            {CloseIcon}
          </span>
        </div>
      </Box>
    </div>
  );
};

export default DailyStatsCalendar;
